import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ReverseImageSearchResult {
  id: number;
  title: string;
  description: string;
  price: number;
  currency: string;
  location: string;
  category: string;
  subcategory: string;
  table_name: string;
  status: string;
  created_at: string;
  updated_at: string;
  view_count: number;
  is_featured: boolean;
  has_images: boolean;
  image_count: number;
  first_image: string | null;
  images: string[];
  user_id: number;
  seller: {
    id: number;
    first_name: string;
    last_name: string;
  };
  similarity_score: number;
  matched_image: string;
  image_description: string;
}

export interface ReverseImageSearchPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface ReverseImageSearchMetadata {
  total_found: number;
  similarity_threshold: number;
  category: string;
  processing_time_ms: number;
  embeddings_compared: number;
}

export interface ImageConversionInfo {
  converted: boolean;
  original_format: string;
  converted_format: string;
  original_size: number;
  converted_size: number;
  compression_ratio: string;
  conversion_strategy: string;
}

export interface ReverseImageSearchResponse {
  status: string;
  data: {
    search_image_url: string;
    results: ReverseImageSearchResult[];
    metadata: ReverseImageSearchMetadata;
    pagination: ReverseImageSearchPagination;
    conversion?: ImageConversionInfo;
  };
}

export interface ReverseImageSearchParams {
  category?: string;
  limit?: number;
  similarity_threshold?: number;
  include_own_listings?: boolean;
}

export interface HealthCheckResponse {
  status: string;
  data: {
    service: string;
    openai_connected: boolean;
    vision_model_available: boolean;
    database_connected: boolean;
    pgvector_available: boolean;
    total_embeddings: number;
    cache_size: number;
    status: 'healthy' | 'unhealthy';
    timestamp: string;
    error?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ReverseImageSearchService {
  private apiUrl = environment.apiUrl || 'http://localhost:3000/api/v1';

  constructor(private http: HttpClient) {}

  uploadAndSearch(
    file: File,
    params: ReverseImageSearchParams = {}
  ): Observable<ReverseImageSearchResponse['data']> {
    const formData = new FormData();
    formData.append('image', file);

    let httpParams = new HttpParams();

    if (params.category) {
      httpParams = httpParams.set('category', params.category);
    }
    if (params.limit) {
      httpParams = httpParams.set('limit', params.limit.toString());
    }
    if (params.similarity_threshold) {
      httpParams = httpParams.set(
        'similarity_threshold',
        params.similarity_threshold.toString()
      );
    }
    if (params.include_own_listings !== undefined) {
      httpParams = httpParams.set(
        'include_own_listings',
        params.include_own_listings.toString()
      );
    }

    return this.http
      .post<ReverseImageSearchResponse>(
        `${this.apiUrl}/reverse-image-search/upload`,
        formData,
        { params: httpParams }
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  searchByUrl(
    imageUrl: string,
    params: ReverseImageSearchParams = {}
  ): Observable<ReverseImageSearchResponse['data']> {
    const body = {
      image_url: imageUrl,
      category: params.category || 'all',
      limit: params.limit || 20,
      similarity_threshold: params.similarity_threshold || 0.7,
      include_own_listings: params.include_own_listings || false,
    };

    return this.http
      .post<ReverseImageSearchResponse>(
        `${this.apiUrl}/reverse-image-search/search-by-url`,
        body
      )
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  healthCheck(): Observable<HealthCheckResponse['data']> {
    return this.http
      .get<HealthCheckResponse>(`${this.apiUrl}/reverse-image-search/health`)
      .pipe(
        map((response) => response.data),
        catchError(this.handleError)
      );
  }

  isEnabled(): Observable<boolean> {
    return this.healthCheck().pipe(
      map((health) => health.status === 'healthy'),
      catchError(() => {
        console.warn('Reverse image search service is not available');
        return [false];
      })
    );
  }

  formatSimilarityScore(score: number): string {
    return `${Math.round(score * 100)}%`;
  }

  getSimilarityBadgeColor(score: number): string {
    if (score >= 0.9) return '#10b981';
    if (score >= 0.8) return '#3b82f6';
    if (score >= 0.7) return '#f59e0b';
    return '#ef4444';
  }

  getSimilarityDescription(score: number): string {
    if (score >= 0.9) return 'Excellent match';
    if (score >= 0.8) return 'Good match';
    if (score >= 0.7) return 'Fair match';
    return 'Poor match';
  }

  validateImageFile(file: File): { valid: boolean; error?: string } {
    const maxSize = 20 * 1024 * 1024;

    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'image/gif',
      'image/bmp',
      'image/tiff',
      'image/tif',
      'image/avif',
      'image/heic',
      'image/heif',
    ];

    const fileType = file.type.toLowerCase();

    if (!allowedTypes.includes(fileType)) {
      return {
        valid: false,
        error: `Unsupported image format: ${this.getFormatDisplayName(
          fileType
        )}. Supported formats: JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF, HEIC/HEIF`,
      };
    }

    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Image file size must be less than 20MB.',
      };
    }

    if (file.size === 0) {
      return {
        valid: false,
        error: 'Selected file appears to be empty.',
      };
    }

    return { valid: true };
  }

  createImagePreview(file: File): string {
    return URL.createObjectURL(file);
  }

  revokeImagePreview(url: string): void {
    URL.revokeObjectURL(url);
  }

  getSupportedFormatsText(): string {
    return 'JPEG, PNG, WebP, GIF, BMP, TIFF, AVIF, HEIC/HEIF';
  }

  isModernFormat(mimeType: string): boolean {
    const modernFormats = [
      'image/avif',
      'image/heic',
      'image/heif',
      'image/webp',
    ];
    return modernFormats.includes(mimeType.toLowerCase());
  }

  hasLimitedBrowserSupport(mimeType: string): boolean {
    const limitedSupportFormats = [
      'image/heic',
      'image/heif',
      'image/avif',
      'image/tiff',
      'image/tif',
      'image/bmp',
    ];
    return limitedSupportFormats.includes(mimeType.toLowerCase());
  }

  requiresConversion(mimeType: string): boolean {
    return (
      this.hasLimitedBrowserSupport(mimeType) || this.isModernFormat(mimeType)
    );
  }

  getFormatDisplayName(mimeType: string): string {
    const formatNames: { [key: string]: string } = {
      'image/jpeg': 'JPEG',
      'image/jpg': 'JPEG',
      'image/png': 'PNG',
      'image/webp': 'WebP',
      'image/gif': 'GIF',
      'image/bmp': 'BMP',
      'image/tiff': 'TIFF',
      'image/tif': 'TIFF',
      'image/avif': 'AVIF',
      'image/heic': 'HEIC',
      'image/heif': 'HEIF',
    };

    const lowerType = mimeType.toLowerCase();
    if (formatNames[lowerType]) {
      return formatNames[lowerType];
    }

    const parts = mimeType.split('/');
    if (parts.length === 2) {
      return parts[1].toUpperCase().replace('X-', '');
    }

    return 'Unknown';
  }

  getFormatDescription(mimeType: string): string {
    const descriptions: { [key: string]: string } = {
      'image/heic':
        "High Efficiency Image Container - Apple's modern image format",
      'image/heif': 'High Efficiency Image File Format - Successor to JPEG',
      'image/avif': 'AV1 Image File Format - Next-generation image format',
      'image/webp': "WebP - Google's modern image format",
      'image/tiff': 'Tagged Image File Format - Professional/archival format',
    };

    return descriptions[mimeType.toLowerCase()] || '';
  }

  private handleError(error: any): Observable<never> {
    let errorMessage = 'Reverse image search failed';

    if (error.error?.message) {
      errorMessage = error.error.message;
    } else if (error.status === 400) {
      errorMessage = 'Invalid image or search parameters';
    } else if (error.status === 413) {
      errorMessage = 'Image file too large. Maximum size is 20MB.';
    } else if (error.status === 415) {
      errorMessage = 'Unsupported image format. Please try a different image.';
    } else if (error.status === 429) {
      errorMessage = 'Too many search requests. Please try again later.';
    } else if (error.status === 503) {
      errorMessage = 'Image search service temporarily unavailable';
    } else if (error.status === 0) {
      errorMessage = 'Unable to connect to image search service';
    }

    console.error('Reverse image search error:', error);
    return throwError(() => new Error(errorMessage));
  }
}
